import React, { useCallback, useEffect, useRef } from 'react'
import { IS_IOS, IS_SAFARI } from '$helper/functions/environmentInfo'
import useDebouncedCallback from '$hooks/use-debounced-callback'
import { AnyToVoidFunction } from '$hooks/types'
import { dateWithOffset } from '$setup/time-offset'

// Used before applying CSS transition
function forceReflow(element: HTMLElement) {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  element.offsetWidth
}

function stopScrollInertia(element: HTMLElement) {
  element.style.display = 'none'
  forceReflow(element)
  element.style.display = ''
}

const MOUSE_WHEEL_DEBOUNCE = 250
const TRIGGER_HEIGHT = 1
const INERTIA_THRESHOLD = 100

export default function useTopOverscroll(
  containerRef: React.RefObject<HTMLDivElement>,
  onOverscroll?: AnyToVoidFunction,
  onReset?: AnyToVoidFunction,
  isDisabled?: boolean,
) {
  // eslint-disable-next-line no-null/no-null
  const overscrollTriggerRef = useRef<HTMLDivElement>(null)

  const isTriggerJustEnabled = useRef(false)
  const lastScrollTopRef = useRef(0)
  const isTriggerEnabledRef = useRef(false)
  const lastIsOnTopRef = useRef(true)
  const lastScrollAtRef = useRef(0)
  const isReturningOverscrollRef = useRef(false)

  const enableOverscrollTrigger = useCallback((noScrollInertiaStop = false) => {
    if (isTriggerEnabledRef.current) return
    if (!overscrollTriggerRef.current || !containerRef.current) return

    overscrollTriggerRef.current.style.display = 'block'
    containerRef.current.scrollTop = TRIGGER_HEIGHT

    if (!IS_SAFARI && !noScrollInertiaStop) {
      stopScrollInertia(containerRef.current)
    }

    isTriggerJustEnabled.current = true
    lastScrollTopRef.current = TRIGGER_HEIGHT
    isTriggerEnabledRef.current = true
    lastIsOnTopRef.current = true
  }, [])

  const disableOverscrollTrigger = useCallback(() => {
    if (!isTriggerEnabledRef.current) return
    if (!overscrollTriggerRef.current) return

    overscrollTriggerRef.current.style.display = 'none'

    isTriggerEnabledRef.current = false
  }, [])

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return

    if (isTriggerJustEnabled.current) {
      isTriggerJustEnabled.current = false

      return
    }

    const newScrollTop = containerRef.current.scrollTop
    const isMovingDown = newScrollTop > lastScrollTopRef.current
    const isMovingUp = newScrollTop < lastScrollTopRef.current
    const isOnTop = newScrollTop === 0
    const lastEventDelay = dateWithOffset() - lastScrollAtRef.current

    if (overscrollTriggerRef.current) {
      if (isOnTop && !isTriggerEnabledRef.current) {
        enableOverscrollTrigger()
        return
      }

      disableOverscrollTrigger()
    }

    if (
      isMovingUp &&
      ((lastIsOnTopRef.current && lastEventDelay > INERTIA_THRESHOLD) ||
        (newScrollTop < 0 && isReturningOverscrollRef.current)) // Overscroll repeated by the user
    ) {
      onOverscroll?.()
    } else if (isMovingDown && newScrollTop > 0) {
      onReset?.()
    }

    lastScrollTopRef.current = newScrollTop
    lastIsOnTopRef.current = isOnTop
    lastScrollAtRef.current = dateWithOffset()
    isReturningOverscrollRef.current = isMovingDown && newScrollTop < 0
  }, [])

  // Handle non-scrollable container
  const handleWheel = useDebouncedCallback(
    (event: WheelEvent) => {
      if (!containerRef.current) return

      const container = containerRef.current

      const isScrollable = container.scrollHeight > container.offsetHeight
      if (isScrollable || event.deltaY === 0) return

      if (event.deltaY < 0) {
        onOverscroll?.()
      } else {
        onReset?.()
      }
    },
    [containerRef, onOverscroll, onReset],
    MOUSE_WHEEL_DEBOUNCE,
  )

  useEffect(() => {
    const container = containerRef.current
    if (!container || isDisabled) return undefined

    if (container.scrollTop === 0) {
      requestAnimationFrame(() => {
        enableOverscrollTrigger(true)
      })
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    container.addEventListener('wheel', handleWheel, { passive: true })

    return () => {
      container.removeEventListener('scroll', handleScroll)
      container.removeEventListener('wheel', handleWheel)
    }
  }, [containerRef, handleWheel, handleScroll, isDisabled])

  return !IS_IOS && !isDisabled ? (
    <div
      ref={overscrollTriggerRef}
      className="overscroll-trigger"
      key="overscroll-trigger"
    />
  ) : undefined
}
